// src/pages/User/Dashboard.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import NoteCard from "../../components/NoteCard";
import SwiftAlert from "../../components/SwiftAlert";
import ShareModal from "../../components/User/ShareModal";
import { useLibraryData } from "../../hooks/useLibraryData";
import { useAppData } from "../../contexts/AppDataContext";
import { checkEnabled, normalizeColorKey } from "../../utils/libraryUtils";
import { updateReviewer, getReviewerById } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { generatePDFDocument } from "../../utils/pdfGenerator";

// --- THEME MAPPING (Required for Move Modal Colors) ---
const LIST_ITEM_VARIANTS = {
  blue: { iconBg: "bg-blue-100", iconText: "text-blue-600", activeBorder: "border-blue-500", activeBg: "bg-blue-50", ring: "ring-blue-500" },
  orange: { iconBg: "bg-orange-100", iconText: "text-orange-600", activeBorder: "border-orange-500", activeBg: "bg-orange-50", ring: "ring-orange-500" },
  purple: { iconBg: "bg-purple-100", iconText: "text-purple-600", activeBorder: "border-purple-500", activeBg: "bg-purple-50", ring: "ring-purple-500" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", activeBorder: "border-emerald-500", activeBg: "bg-emerald-50", ring: "ring-emerald-500" },
  red: { iconBg: "bg-red-100", iconText: "text-red-600", activeBorder: "border-red-500", activeBg: "bg-red-50", ring: "ring-red-500" },
  gray: { iconBg: "bg-slate-100", iconText: "text-slate-600", activeBorder: "border-slate-500", activeBg: "bg-slate-50", ring: "ring-slate-500" },
};

// --- UTILITY: Time Formatter ---
const formatStudyTime = (totalSeconds) => {
  if (!totalSeconds || isNaN(totalSeconds)) return "0m";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes === 0 && totalSeconds > 0) return "< 1m";
  return `${minutes}m`;
};

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ✅ Get user from AuthContext (cookie-based)
  const { user, loading: authLoading } = useAuth();

  // --- Modals & Alert State ---
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [noteToMove, setNoteToMove] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);
  const [alert, setAlert] = useState(null);

  const navigate = useNavigate();

  // ✅ Get cached data from global AppDataContext
  const { folders, recentNotes, loading, setRecentNotes, markNoteAccessed } = useLibraryData();
  const { stats } = useAppData();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // ✅ Optional safety redirect
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Map Folder IDs to Names for NoteCards
  const folderMap = useMemo(() => {
    const map = {};
    if (Array.isArray(folders)) {
      folders.forEach((f) => {
        if (f._id) map[f._id] = f.name;
        if (f.id) map[f.id] = f.name;
      });
    }
    return map;
  }, [folders]);

  // --- HANDLERS ---
  const handleRenameNote = async (note, newTitle) => {
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    const isDuplicate = recentNotes.some(
      (n) =>
        (n.title || n.topic || "").trim().toLowerCase() === cleanTitle.toLowerCase() &&
        n._id !== note._id
    );

    if (isDuplicate) {
      setAlert({ type: "warning", title: "Name Taken", message: "That name is already in use." });
      return;
    }

    try {
      const updatedNotes = recentNotes.map((n) =>
        n._id === note._id ? { ...n, title: cleanTitle, topic: cleanTitle } : n
      );
      if (setRecentNotes) setRecentNotes(updatedNotes);

      await updateReviewer(note._id, { title: cleanTitle });
      setAlert({ type: "success", title: "Renamed", message: "Note renamed successfully." });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Failed",
        message: error?.message || "Failed to rename note.",
      });
    }
  };

  const handleColorChange = async (note, newColor) => {
    try {
      const updatedNotes = recentNotes.map((n) => (n._id === note._id ? { ...n, color: newColor } : n));
      if (setRecentNotes) setRecentNotes(updatedNotes);

      await updateReviewer(note._id, { color: newColor });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to update color." });
    }
  };

  const openMoveModal = (note) => {
    setNoteToMove(note);
    setIsMoveModalOpen(true);
  };

  const openShareModal = (note) => {
    setNoteToShare(note);
    setIsShareModalOpen(true);
  };

  const handleArchiveNote = async (note) => {
    try {
      if (setRecentNotes) {
        setRecentNotes((prev) => prev.filter((n) => (n._id || n.id) !== (note._id || note.id)));
      }
      await updateReviewer(note._id || note.id, { archived: true });
      setAlert({
        type: "success",
        title: "Archived",
        message: `"${note.title || note.topic}" moved to archive.`,
      });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to archive note." });
    }
  };

  const handleShareModalUpdateNote = (updatedNote) => {
    setRecentNotes((previous) =>
      previous.map((note) =>
        String(note._id || note.id) === String(updatedNote._id || updatedNote.id)
          ? updatedNote
          : note
      )
    );
  };

  const handleMoveSubmit = async (targetFolderId) => {
    if (!noteToMove) return;
    try {
      await updateReviewer(noteToMove._id, { folderId: targetFolderId });

      const updatedNotes = recentNotes.map((n) =>
        n._id === noteToMove._id ? { ...n, folder: targetFolderId } : n
      );
      if (setRecentNotes) setRecentNotes(updatedNotes);

      setIsMoveModalOpen(false);
      setNoteToMove(null);
      setAlert({ type: "success", title: "Moved", message: "Note moved successfully." });
    } catch (error) {
      setAlert({ type: "error", title: "Failed", message: "Failed to move note." });
    }
  };

  const handleStudyNavigation = (file, requestedMode) => {
    const fileId = file?._id || file?.id;
    if (fileId && typeof markNoteAccessed === "function") {
      markNoteAccessed(fileId);
    }

    const cfg = file?.config || {};
    let mode = requestedMode;

    if (!mode) {
      if (checkEnabled(cfg.summaryEnabled)) mode = "summary";
      else if (checkEnabled(cfg.flashcardsEnabled)) mode = "flashcards";
      else if (checkEnabled(cfg.quizEnabled)) mode = "quiz";
    }
    if (!mode) return;

    navigate("/study-mode", {
      state: {
        mode,
        noteData: {
          ...file,
          _id: file?._id,
          id: file?._id || file?.id,
          title: file?.title || file?.topic || "Untitled",
          subject: file?.meta || "Library",
          hasSummary: checkEnabled(cfg.summaryEnabled),
          hasFlashcards: checkEnabled(cfg.flashcardsEnabled),
          hasQuiz: checkEnabled(cfg.quizEnabled),
          config: cfg,
        },
        reviewerId: file?._id || file?.id,
      },
    });
  };

  const handleDownloadPdf = async (note) => {
    const reviewerId = note?._id || note?.id;
    if (!reviewerId) {
      setAlert({ type: "error", title: "Failed", message: "Reviewer ID is missing." });
      return;
    }

    try {
      const raw = await getReviewerById(reviewerId);
      const normalized = raw && raw.data && raw.status ? raw.data : raw;
      const meta = normalized?.meta || normalized?.data?.meta || {};
      const data =
        normalized?.meta && normalized?.data
          ? normalized.data
          : normalized?.data?.data ?? normalized?.data ?? normalized;

      const summary =
        (typeof data === "string" ? data : "") ||
        data?.summaryHtml ||
        data?.summary ||
        data?.html ||
        data?.content ||
        "";

      const flashcardsRaw = data?.flashcards || data?.flashCards || data?.cards || [];
      const flashcards = Array.isArray(flashcardsRaw)
        ? flashcardsRaw
          .map((c) => ({
            front: c?.front || c?.term || c?.question || c?.q || c?.title || "",
            back: c?.back || c?.definition || c?.answer || c?.a || c?.content || "",
          }))
          .filter((c) => c.front || c.back)
        : [];

      const quizRaw = data?.quiz || data?.questions || [];
      const quiz = Array.isArray(quizRaw)
        ? quizRaw
          .map((q) => {
            const optsRaw = q?.options || q?.choices || q?.answers || [];
            const options = Array.isArray(optsRaw)
              ? optsRaw
                .map((o) => (typeof o === "string" ? o : o?.text || o?.label || ""))
                .filter(Boolean)
              : [];
            return {
              question: q?.question || q?.prompt || q?.q || "",
              options,
              answer: q?.answer || q?.correctAnswer || q?.correct || "",
              explanation: q?.explanation || q?.reason || "",
            };
          })
          .filter((q) => q.question)
        : [];

      if (!summary && flashcards.length === 0 && quiz.length === 0) {
        setAlert({ type: "warning", title: "No Content", message: "This reviewer has no downloadable content yet." });
        return;
      }

      const cfg = { ...(meta?.config || {}), ...(note?.config || {}) };
      generatePDFDocument({
        title: note?.title || note?.topic || meta?.topic || "Reviewer",
        generatedData: { summary, flashcards, quiz },
        config: {
          ...cfg,
          summaryEnabled: checkEnabled(cfg.summaryEnabled),
          flashcardsEnabled: checkEnabled(cfg.flashcardsEnabled),
          quizEnabled: checkEnabled(cfg.quizEnabled),
        },
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Failed",
        message: error?.message || "Failed to download PDF.",
      });
    }
  };

  if (authLoading) return <div className="p-6 text-slate-400 font-bold">Loading...</div>;
  if (!user) return null;

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} title="Dashboard" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full space-y-8 animate-fade-in pb-20 md:pb-10">
            {/* HERO SECTION */}
            <section className="bg-gradient-to-r from-blue-700 to-blue-600 rounded-3xl p-6 md:p-8 text-white relative overflow-hidden shadow-xl shadow-blue-900/20 flex flex-col justify-center">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                <div className="max-w-2xl text-left">
                  <div className="inline-block px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 text-blue-100 border border-white/20">
                    Welcome Back, {user.firstName || "Scholar"}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">Ready to ace your exams?</h1>
                  <p className="text-blue-100 text-sm md:text-base leading-relaxed mb-8 max-w-lg">
                    Upload your PDFs, documents, text files, or notes to instantly generate comprehensive interactive reviewers.
                  </p>
                  <button
                    onClick={() => navigate("/create-reviewer")}
                    className="bg-white text-blue-700 px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-blue-900/20 hover:shadow-xl hover:bg-blue-50 hover:-translate-y-1 transition-all inline-flex items-center"
                  >
                    <i className="fa-solid fa-wand-magic-sparkles mr-2"></i> Create Reviewer
                  </button>
                </div>

                <div className="hidden md:flex relative">
                  <div className="w-32 h-32 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center transform rotate-6 hover:rotate-0 transition duration-500 shadow-2xl">
                    <i className="fa-solid fa-file-contract text-5xl text-white/90"></i>
                  </div>
                </div>
              </div>
            </section>

            {/* STATS GRID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                icon="fa-fire"
                color="orange"
                label="Day Streak"
                value={`${stats.streak} ${stats.streak === 1 ? "Day" : "Days"}`}
                subtext="Target: 2 min/day"
                animateIcon={stats.streak > 0}
              />
              <StatCard icon="fa-layer-group" color="blue" label="Total Notes" value={recentNotes.length || 0} subtext="Generated Reviewers" />
              <StatCard icon="fa-clock-rotate-left" color="purple" label="Total Study Time" value={formatStudyTime(stats.totalSeconds)} subtext="Lifetime usage" />
            </div>

            {/* STUDY GROUP */}
            <div
              onClick={() => navigate("/study-group")}
              className="group cursor-pointer bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 relative overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <div className="absolute right-0 top-0 h-full w-1/2 bg-white/5 skew-x-12 transform translate-x-12 group-hover:translate-x-8 transition-transform"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 group-hover:bg-white/20 transition flex-shrink-0">
                    <i className="fa-solid fa-people-group text-white text-2xl"></i>
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white mb-1">Study Group</h3>
                    <p className="text-slate-400 text-sm">Collaborate and study together in real time.</p>
                  </div>
                </div>
                <button onClick={() => navigate("/study-group")} className="w-full md:w-auto bg-white text-slate-900 px-6 py-2.5 rounded-xl text-sm font-bold shadow-md group-hover:bg-blue-50 transition flex items-center justify-center gap-2 whitespace-nowrap">
                  Join Group <i className="fa-solid fa-arrow-right"></i>
                </button>
              </div>
            </div>

            {/* RECENT ACTIVITY */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Recent Activity</h3>
                <button onClick={() => navigate("/library")} className="text-sm text-blue-600 font-bold hover:text-blue-800 transition">
                  View Library <i className="fa-solid fa-arrow-right ml-1 text-xs"></i>
                </button>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />
                  ))}
                </div>
              ) : recentNotes.length === 0 ? (
                <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-sm">
                  <div className="text-3xl text-slate-300 mb-2">
                    <i className="fa-solid fa-box-open"></i>
                  </div>
                  <h3 className="font-bold text-slate-800">No notes found</h3>
                  <p className="text-sm text-slate-400">Create a reviewer to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {recentNotes.slice(0, 4).map((note) => (
                    <NoteCard
                      key={note._id || note.id}
                      note={note}
                      folderName={folderMap[note.folder] || folderMap[note.folder?._id] || "Uncategorized"}
                      onStudy={handleStudyNavigation}
                      onRename={handleRenameNote}
                      onColorChange={handleColorChange}
                      onMove={openMoveModal}
                      onDownloadPdf={handleDownloadPdf}
                      onShare={openShareModal}
                      onArchive={handleArchiveNote}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* MOVE MODAL */}
      {isMoveModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Move to Folder</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select a destination for <b>{noteToMove?.title || noteToMove?.topic}</b>
            </p>

            <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-1">
              <button
                onClick={() => handleMoveSubmit(null)}
                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                  <i className="fa-solid fa-folder-minus"></i>
                </div>
                <span className="font-semibold text-gray-700">No Folder</span>
              </button>

              {folders.map((f) => {
                const isActive = noteToMove?.folder === f._id || noteToMove?.folder?._id === f._id;
                const theme = LIST_ITEM_VARIANTS[normalizeColorKey(f.color)] || LIST_ITEM_VARIANTS.blue;

                return (
                  <button
                    key={f._id}
                    onClick={() => handleMoveSubmit(f._id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${isActive ? `${theme.activeBorder} ${theme.activeBg} ring-1 ${theme.ring}` : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.iconBg} ${theme.iconText}`}>
                      <i className="fa-solid fa-folder"></i>
                    </div>
                    <span className="font-semibold text-gray-700 truncate">{f.name}</span>
                    {isActive && <i className={`fa-solid fa-check ${theme.iconText} ml-auto`}></i>}
                  </button>
                );
              })}
            </div>

            <button onClick={() => setIsMoveModalOpen(false)} className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition">
              Cancel
            </button>
          </div>
        </div>
      )}

      {isShareModalOpen && noteToShare && (
        <ShareModal
          note={noteToShare}
          onClose={() => { setIsShareModalOpen(false); setNoteToShare(null); }}
          onUpdateNote={handleShareModalUpdateNote}
          onAlert={setAlert}
        />
      )}

      {/* ALERT */}
      {alert && <SwiftAlert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
    </div>
  );
};

// --- StatCard Component ---
const StatCard = ({ icon, color, label, value, subtext, animateIcon }) => {
  const styles = {
    orange: { bg: "bg-orange-50", text: "text-orange-500" },
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    purple: { bg: "bg-purple-50", text: "text-purple-600" },
  };
  const theme = styles[color] || styles.blue;

  return (
    <div className="bg-white border border-slate-100 p-4 md:p-5 rounded-2xl flex flex-row items-center gap-4 shadow-sm hover:shadow-md transition-all h-full">
      <div className={`w-14 h-14 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center text-2xl flex-shrink-0 ${animateIcon ? "animate-pulse" : ""}`}>
        <i className={`fa-solid ${icon}`}></i>
      </div>
      <div className="text-left flex-1 min-w-0">
        <p className="text-[10px] md:text-xs text-slate-400 font-bold uppercase tracking-wider truncate mb-0.5">{label}</p>
        <p className="text-xl md:text-2xl font-bold text-slate-800 truncate">{value}</p>
        {subtext && <p className="text-[10px] text-slate-400 font-medium mt-0.5 whitespace-normal leading-tight">{subtext}</p>}
      </div>
    </div>
  );
};

export default Dashboard;
